import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaEye, FaSearch, FaFilter, FaSync, FaCalendarAlt } from "react-icons/fa";
import { getAttendanceReports } from "../../services/schoolService";
import "../teacher/TeacherLayer.css";

const ClasswiseAttendanceLayer = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const [fastFilter, setFastFilter] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { action: "class_view" };
      if (debouncedSearch) {
        params.search = debouncedSearch;
        params.search_class = debouncedSearch;
      }
      
      if (fastFilter !== "custom") {
        params.date_period = fastFilter;
      } else {
        params.date_period = "custom";
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }

      const res = await getAttendanceReports(params);
      if (res?.status && res?.classes_data) {
        setClasses(res.classes_data);
      } else {
        setClasses([]);
      }
    } catch (error) {
      console.error(error);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fastFilter, fromDate, toDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredClasses = classes.filter(cls => {
    const stats = cls.summary_stats || cls.summary_counters || {};
    const notMarked = stats.not_marked ?? 0;
    const isSubmitted = notMarked === 0;
    const statusStr = isSubmitted ? "Submitted" : "Pending";
    const matchesStatus = filterStatus === "" || statusStr === filterStatus;
    
    const classNameStr = cls.class_name || "";
    const matchesSearch = !searchTerm || classNameStr.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredClasses.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentClasses = filteredClasses.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header">
            <div>
              <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
                Attendance Monitoring
              </h6>
              <p className="text-muted mt-1 mb-0 ms-3" style={{ fontSize: "13px" }}>
                Class-wise Attendance Status
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle" onClick={fetchData} style={{ width: '40px', height: '40px' }} title="Refresh">
                <FaSync className="text-secondary" />
              </button>
            </div>
          </div>

          <div className="bg-light p-3 rounded-3 mb-4 mt-4 border" style={{ borderColor: '#e9ecef' }}>
            <h6 className="mb-3 d-flex align-items-center gap-2 text-secondary">
              <FaFilter /> Filters
            </h6>
            
            {/* Row 1 */}
            <div className="row g-3 align-items-end mb-3">
              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Search Class</label>
                <div className="position-relative">
                  <FaSearch className="position-absolute text-muted" style={{ top: "14px", left: "14px" }} />
                  <input
                    type="text"
                    placeholder="E.g., Class 10th"
                    className="form-control form-control-lg bg-light border-0 shadow-none ps-5"
                    style={{ borderRadius: '10px', fontSize: '15px' }}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Date Period</label>
                <select 
                  className="form-select form-select-lg bg-light border-0 shadow-none"
                  style={{ borderRadius: '10px', fontSize: '15px' }}
                  value={fastFilter}
                  onChange={(e) => {
                    setFastFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="today">Today</option>
                  <option value="7_days">Last 7 Days</option>
                  <option value="this_month">This Month</option>
                  <option value="custom">Custom Date</option>
                </select>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaCalendarAlt className="me-1" /> From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setFastFilter("custom");
                    setCurrentPage(1);
                  }}
                  className="form-control form-control-lg bg-light border-0 shadow-none text-center"
                  style={{ borderRadius: '10px', fontSize: '15px' }}
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}><FaCalendarAlt className="me-1" /> To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setFastFilter("custom");
                    setCurrentPage(1);
                  }}
                  className="form-control form-control-lg bg-light border-0 shadow-none text-center"
                  style={{ borderRadius: '10px', fontSize: '15px' }}
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-6">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Status</label>
                <select 
                  className="form-select form-select-lg bg-light border-0 shadow-none"
                  style={{ borderRadius: '10px', fontSize: '15px' }}
                  value={filterStatus} 
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div className="col-12 col-md-6">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Show Limit</label>
                <select 
                  className="form-select form-select-lg bg-light border-0 shadow-none"
                  style={{ borderRadius: '10px', fontSize: '15px' }}
                  value={recordsPerPage} 
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 Entries</option>
                  <option value={25}>25 Entries</option>
                  <option value={50}>50 Entries</option>
                  <option value={100}>100 Entries</option>
                </select>
              </div>
            </div>
          </div>

          <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table">
            <table className="user-table text-center align-middle">
              <thead className="text-center text-nowrap">
                <tr>
                  <th>Class Name</th>
                  <th>Total Students</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Late</th>
                  <th>Half Day</th>
                  <th>Leave</th>
                  <th>Not Marked</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody className="text-center text-nowrap">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">Loading classes...</td>
                  </tr>
                ) : currentClasses.length > 0 ? (
                  currentClasses.map((cls) => {
                    const stats = cls.summary_stats || cls.summary_counters || {};
                    const total = stats.total_students ?? stats.total_students_matched ?? 0;
                    const present = stats.present ?? 0;
                    const absent = stats.absent ?? 0;
                    const late = stats.late ?? 0;
                    const halfDay = stats.half_day ?? 0;
                    const leave = stats.leave ?? 0;
                    const notMarked = stats.not_marked ?? 0;
                    const isSubmitted = notMarked === 0;
                    const statusStr = isSubmitted ? "Submitted" : "Pending";
                    return (
                      <tr key={cls.class_id} className="smooth-table-row">
                        <td className="fw-bold text-primary">{cls.class_name}</td>
                        <td>{total}</td>
                        <td className="text-success fw-bold">{present}</td>
                        <td className="text-danger fw-bold">{absent}</td>
                        <td className="text-warning fw-bold">{late}</td>
                        <td className="text-info fw-bold">{halfDay}</td>
                        <td className="fw-bold" style={{ color: "#7e22ce" }}>{leave}</td>
                        <td className="text-secondary fw-bold">{notMarked}</td>
                        <td>
                          <span className={`badge rounded-pill px-3 py-1 ${isSubmitted ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25' : 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25'}`}>
                            {statusStr}
                          </span>
                        </td>
                        <td>
                          <Link to={`/attendance/class/${cls.class_id}`} className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 rounded-pill">
                            <FaEye /> View
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">No data available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {!loading && filteredClasses.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <span className="text-muted" style={{ fontSize: '14px' }}>
                Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredClasses.length)} of {filteredClasses.length} entries
              </span>
              <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link shadow-none rounded-start-pill" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                  <button className="page-link shadow-none rounded-end-pill" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ClasswiseAttendanceLayer;
